import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import type { SubjectKind } from '@hp/domain';
import type { SyncRunKind } from '@hp/domain';
import { invalidatePipelineState } from './queries';
import { trpc } from '../../api/trpc';
import { invalidateTrpcPipeline, useDedupRuns, useSyncRuns } from './queries';
import { EntityBadge, StatusBadge } from '../../components/badges';
import { ErrorAlert } from '../../components/error-alert';
import { Disclosure } from '../../components/ui/disclosure';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { RelativeTime } from '../../components/relative-time';
import { prettyJson, statsSummary } from '../../lib/format';

// --- e) Manual controls & run history (the old dashboard content) ---

function StatsCell({ error, stats }: { error: string | null; stats: unknown }) {
  if (error) {
    return (
      <td className="bo-cell-error">
        <span className="bo-clip" title={error}>
          {error}
        </span>
      </td>
    );
  }
  return (
    <td className="bo-cell-note">
      <span className="bo-clip" title={prettyJson(stats)}>
        {statsSummary(stats)}
      </span>
    </td>
  );
}

function RunHistoryTables() {
  const { t } = useTranslation(['pipeline', 'common']);
  const syncRuns = useSyncRuns();
  const dedupRuns = useDedupRuns();
  return (
    <div className="bo-history-grid">
      <section className="hp-card">
        <HpCardHeader title={t('manual.syncRuns')} icon="refresh" />
        <ErrorAlert error={syncRuns.error} className="bo-alert--inset" />
        <TableScroll label={t('manual.syncRuns')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('common:table.id')}</th>
              <th>{t('manual.kind')}</th>
              <th>{t('common:table.status')}</th>
              <th>{t('common:table.started')}</th>
              <th>{t('common:table.finished')}</th>
              <th>{t('common:table.stats')}</th>
            </BrutTheadRow>
            <tbody>
              {(syncRuns.data ?? []).slice(0, 10).map((run) => (
                <tr key={run.id}>
                  <td className="bo-cell-muted">{run.id}</td>
                  <td>
                    <span className="bo-row">
                      {t(`syncKind.${run.kind}`)}
                      {run.trigger === 'schedule' && <HpTag tone="neutral">{t('trigger.schedule')}</HpTag>}
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={run.status} />
                  </td>
                  <td>
                    <RelativeTime value={run.startedAt ?? run.createdAt} />
                  </td>
                  <td>
                    <RelativeTime value={run.finishedAt} />
                  </td>
                  <StatsCell error={run.error} stats={run.stats} />
                </tr>
              ))}
              {syncRuns.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="bo-cell-empty">
                    {t('manual.noSyncRuns')}
                  </td>
                </tr>
              )}
            </tbody>
          </BrutTable>
        </TableScroll>
      </section>

      <section className="hp-card">
        <HpCardHeader title={t('manual.dedupRuns')} icon="users" />
        <ErrorAlert error={dedupRuns.error} className="bo-alert--inset" />
        <TableScroll label={t('manual.dedupRuns')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('common:table.id')}</th>
              <th>{t('manual.entity')}</th>
              <th>{t('common:table.status')}</th>
              <th>{t('common:table.started')}</th>
              <th>{t('common:table.finished')}</th>
              <th>{t('common:table.stats')}</th>
            </BrutTheadRow>
            <tbody>
              {(dedupRuns.data ?? []).slice(0, 10).map((run) => (
                <tr key={run.id}>
                  <td className="bo-cell-muted">{run.id}</td>
                  <td>
                    <EntityBadge entityType={run.entityType} />
                  </td>
                  <td>
                    <StatusBadge status={run.status} />
                  </td>
                  <td>
                    <RelativeTime value={run.startedAt ?? run.createdAt} />
                  </td>
                  <td>
                    <RelativeTime value={run.finishedAt} />
                  </td>
                  <StatsCell error={run.error} stats={run.stats} />
                </tr>
              ))}
              {dedupRuns.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="bo-cell-empty">
                    {t('manual.noDedupRuns')}
                  </td>
                </tr>
              )}
            </tbody>
          </BrutTable>
        </TableScroll>
      </section>
    </div>
  );
}

export function ManualControls({ canRun }: { canRun: boolean }) {
  const { t } = useTranslation(['pipeline', 'common']);
  const startSync = useMutation(
    trpc.pipedrive.startSync.mutationOptions({
      onSuccess: () => Promise.all([invalidateTrpcPipeline(), invalidatePipelineState()]),
    }),
  );
  const startSyncKind = (kind: SyncRunKind): void => startSync.mutate({ kind });
  const syncBusy = (kind: SyncRunKind): boolean => startSync.isPending && startSync.variables?.kind === kind;
  const startDedup = useMutation(
    trpc.dedup.startRun.mutationOptions({
      onSuccess: () => Promise.all([invalidateTrpcPipeline(), invalidatePipelineState()]),
    }),
  );
  const startDedupFor = (entityType: SubjectKind): void => startDedup.mutate({ entityType });
  const dedupBusy = (entityType: SubjectKind): boolean =>
    startDedup.isPending && startDedup.variables?.entityType === entityType;

  return (
    <Disclosure title={t('manual.title')} caption={t('manual.caption')}>
      <div className="bo-sections">
        {canRun && (
          <div className="bo-sections">
            <div className="bo-row">
              <HpButton
                icon="refresh"
                busy={syncBusy('delta')}
                disabled={startSync.isPending}
                onClick={() => startSyncKind('delta')}
              >
                {t('manual.refreshDelta')}
              </HpButton>
              <HpButton
                icon="download"
                busy={syncBusy('full')}
                disabled={startSync.isPending}
                onClick={() => {
                  if (window.confirm(t('confirm.fullRefresh'))) startSyncKind('full');
                }}
              >
                {t('manual.refreshFull')}
              </HpButton>
              <HpButton
                icon="shield"
                busy={syncBusy('backup')}
                disabled={startSync.isPending}
                onClick={() => startSyncKind('backup')}
              >
                {t('manual.snapshot')}
              </HpButton>
              <HpButton
                icon="users"
                busy={dedupBusy('person')}
                disabled={startDedup.isPending}
                onClick={() => startDedupFor('person')}
              >
                {t('manual.dedupPersons')}
              </HpButton>
              <HpButton
                icon="building"
                busy={dedupBusy('organization')}
                disabled={startDedup.isPending}
                onClick={() => startDedupFor('organization')}
              >
                {t('manual.dedupOrgs')}
              </HpButton>
            </div>
            <ErrorAlert error={startSync.error ?? startDedup.error} />
          </div>
        )}
        <RunHistoryTables />
      </div>
    </Disclosure>
  );
}
